import { SessionEndedError, isSessionRejection } from '@/services/sessionErrors';
import { centralEnabled, centralManager, centralSession, centralSignOut } from '@/services/centralAuth';
import { demoEnabled } from "@/config/api";
import { createContext, useContext, useEffect, useMemo, useState, useRef } from "react";
import type { ReactNode } from "react";
import type { Session } from "@/models/auth";
import { readSession, watchSession, writeSession } from "@/helpers/storage";
import { readError, subscribeSession } from "@/services/apiClient";
import {
  selectBusiness,
  signOut as clearAuth,
} from "@/services/authService";
interface AuthContextValue {
  session: Session | null;
  ready: boolean;
  accessError: string | null;
  retryAccess: () => void;
  startDemo: () => void;
  isDemo: boolean;
  selectBusiness: (businessUserId: string) => Promise<Session>;
  signOut: () => void;
}
const demoSession: Session = {
  accessToken: "",
  refreshToken: "",
  expiresAt: null,
  requiresBusinessSelection: false,
  purpose: "access",
  userId: "demo",
  businessId: "demo",
  businessUserId: "demo",
  emailAddress: "alex@example.test",
  fullName: "Alex Morgan",
  role: "CRMAccountAdmin",
  businesses: [
    {
      businessUserId: "demo",
      businessId: "demo",
      businessName: "Demo brewery",
      role: "CRMAccountAdmin",
    },
    {
      businessUserId: "demo-wholesale",
      businessId: "demo-wholesale",
      businessName: "Demo wholesale",
      role: "CRMAccountAdmin",
    },
  ],
};
const AuthContext = createContext<AuthContextValue | null>(null);
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isDemo, setIsDemo] = useState(
    demoEnabled && sessionStorage.getItem("crm-demo") === "true",
  );
  const [ready, setReady] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const sessionRevision = useRef(0);
  const updateSession = (next: Session | null) => { sessionRevision.current++; setSession(next); };
  useEffect(() => subscribeSession(updateSession), []);
  useEffect(() => watchSession(updateSession), []);
  useEffect(() => {
    const ended = () => { clearAuth(); updateSession(null) }
    window.addEventListener('crm-session-ended', ended)
    return () => window.removeEventListener('crm-session-ended', ended)
  }, []);
  useEffect(() => {
    if (demoEnabled && sessionStorage.getItem("crm-demo") === "true") {
      const chosen = demoSession.businesses.find(
        (b) => b.businessUserId === sessionStorage.getItem("crm-demo-business"),
      );
      setSession(
        chosen
          ? {
              ...demoSession,
              businessId: chosen.businessId,
              businessUserId: chosen.businessUserId,
              role: chosen.role,
            }
          : demoSession,
      );
      setReady(true);
      return;
    }
    {
      const revision = sessionRevision.current;
      setAccessError(null); setReady(false);
      centralManager.getUser().then(user => user ? centralSession(readSession()?.businessUserId ?? localStorage.getItem('crm-last-membership')) : null)
        .then(next => { if (sessionRevision.current !== revision) return; if (next) writeSession(next); setSession(next); })
        .catch(error => {
          if (sessionRevision.current !== revision) return;
          if (error instanceof SessionEndedError || isSessionRejection(error)) {
            sessionStorage.setItem('crm-auth-error', readError(error));
            clearAuth(); setSession(null);
          } else setAccessError(readError(error));
        })
        .finally(() => setReady(true));
      return;
    }
  }, [attempt]);
  useEffect(() => {
    if (!centralEnabled || isDemo) return;
    let active = true;
    let pending = false;
    const refreshProfile = async () => {
      const before = readSession();
      if (!before || pending || document.visibilityState !== 'visible') return;
      pending = true;
      try {
        const updated = await centralSession(before.businessUserId);
        const current = readSession();
        if (active && current?.userId === before.userId && current?.businessUserId === before.businessUserId) {
          writeSession({ ...current, fullName: updated.fullName, emailAddress: updated.emailAddress });
        }
      } catch { /* The next authenticated request handles session/access errors. */ }
      finally { pending = false; }
    };
    window.addEventListener('focus', refreshProfile);
    return () => { active = false; window.removeEventListener('focus', refreshProfile); };
  }, [isDemo]);
  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      ready,
      accessError,
      retryAccess: () => setAttempt(x => x + 1),
      isDemo,
      startDemo: () => {
        if (!demoEnabled) return;
        sessionStorage.setItem("crm-demo", "true");
        setIsDemo(true);
        sessionStorage.removeItem("crm-demo-business");
        setSession(demoSession);
      },
      selectBusiness: async (businessUserId) => {
        try {
          if (isDemo) {
            const business = demoSession.businesses.find(
              (b) => b.businessUserId === businessUserId,
            );
            if (!business) throw new Error("That business is unavailable.");
            const next = {
              ...demoSession,
              businessId: business.businessId,
              businessUserId: business.businessUserId,
              role: business.role,
            };
            sessionStorage.setItem("crm-demo-business", businessUserId);
            setSession(next);
            return next;
          }
          const selected = await selectBusiness(businessUserId);
          const next = {
            ...selected,
            businesses: selected.businesses.length
              ? selected.businesses
              : (session?.businesses ?? []),
          };
          writeSession(next);
          setSession(next);
          return next;
        } catch (error) {
          throw new Error(readError(error));
        }
      },
      signOut: () => {
        if (centralEnabled && !isDemo) { void centralSignOut().catch(() => window.alert('Unable to sign out of Accounts. Please retry.')); return; }
        sessionStorage.removeItem("crm-demo");
        sessionStorage.removeItem("crm-demo-business");
        setIsDemo(false);
        clearAuth();
        setSession(null);
      },
    }),
    [ready, session, isDemo, accessError],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
